"use client"

import { Keyboard, QrCode } from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"
import type { InstallMethod } from "../types"

/** Methods this toggle exposes. Deeplink lives in its own primary button. */
type ToggleMethod = Extract<InstallMethod, "qr" | "manual">

type Props = LocaleProps & {
  method: ToggleMethod
  onChange: (method: ToggleMethod) => void
}

const ENTRIES: { id: ToggleMethod; labelKey: string; Icon: typeof QrCode }[] = [
  { id: "qr", labelKey: "install.method.qr", Icon: QrCode },
  { id: "manual", labelKey: "install.method.manual", Icon: Keyboard },
]

/**
 * QR vs Manual toggle inside the install card. Smaller than DeviceTabs (this
 * is a sub-choice) but same segmented language so the visual hierarchy is
 * obvious: pick OS → pick method.
 */
export function InstallMethodToggle({ lng, method, onChange }: Props) {
  return (
    <div
      role="tablist"
      aria-label={accountText(lng, "install.method.ariaLabel")}
      className="flex items-stretch gap-1 rounded-xl bg-stone-100 p-1"
    >
      {ENTRIES.map(({ id, labelKey, Icon }) => {
        const active = method === id
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            className={`flex flex-1 min-h-10 items-center justify-center gap-2 rounded-lg text-xs font-bold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
              active
                ? "bg-white text-orange-600 shadow-[0_1px_4px_-1px_rgba(28,25,23,0.12)]"
                : "bg-transparent text-stone-600 hover:text-stone-800"
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden />
            <span>{accountText(lng, labelKey)}</span>
          </button>
        )
      })}
    </div>
  )
}
